import { Box, Drawer, IconButton, Stack, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useUserContext } from 'src/auth/useUserContext';
import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo/Logo';
import { NavSectionMini, NavSectionVertical } from 'src/components/nav-section';
import Scrollbar from 'src/components/scrollbar';
import { HEADER, NAV } from 'src/config-global';
import useResponsive from 'src/hooks/useResponsive';
import { useSidebarContext } from 'src/layouts/dashboard/SidebarContext';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useCurrentVisitContext } from 'src/sections/@dashboard/visit/hooks/useCurrentVisitContext';
import {
  canWriteStock,
  isWarehouseVisible,
  useWarehouseActivation,
} from 'src/sections/@dashboard/warehouse/hooks/useWarehouseActivation';
import { DARK_BACKGROUND } from 'src/theme/palette';
import { Roles } from 'src/types/User';
import getNavConfig from './config-navigation';
import NavToggleButton from './NavToggleButton';
import getUserNavConfig from './user-config-navigation';
// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({
  openNav,
  onCloseNav,
}: Props): JSX.Element {
  const { sidebarOpen, toggleSidebar, isProSidebar } = useSidebarContext();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { pendingVisits } = useCurrentVisitContext();
  const {
    isUserRole,
    isProfessionalRole,
    isOrganizationAdminRole,
    isOrganizationOwnerRole,
    data: userData,
  } = useUserContext();
  const { selectedRole, isOrganizationAdminWithoutOrganizations } =
    useOrganizationContext();
  const isLive =
    isProfessionalRole &&
    selectedRole === Roles.professional &&
    pendingVisits.length > 0;
  const { data: warehouseActivation } = useWarehouseActivation();
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const navData = isUserRole
    ? getUserNavConfig()
    : getNavConfig({
        isLive,
        showWarehouse: isWarehouseVisible(warehouseActivation),
        warehouseEnabled: canWriteStock(warehouseActivation),
        access: {
          isOrganizationAdmin: isOrganizationAdminRole,
          isOrganizationOwner: isOrganizationOwnerRole,
          isProfessional: isProfessionalRole,
          selectedRole,
        },
      });

  const renderDefaultContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo variant="blue" />
      </Stack>

      {!isOrganizationAdminWithoutOrganizations && userData?.onboarded && (
        <NavSectionVertical data={navData} />
      )}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  const contentTransition = theme.transitions.create('opacity', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.shorter,
  });

  const renderProSidebarContent = (
    <Box sx={{ position: 'relative', height: 1 }}>
      {/* Expanded content */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? 'auto' : 'none',
          transition: contentTransition,
        }}
      >
        <Scrollbar
          sx={{
            height: 1,
            '& .simplebar-content': {
              height: 1,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ pt: '16px' }} />

          {!isOrganizationAdminWithoutOrganizations && userData?.onboarded && (
            <NavSectionVertical data={navData} />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: 'grey.400',
                bgcolor: 'grey.800',
                '&:hover': { bgcolor: 'grey.700' },
              }}
            >
              <Iconify icon="eva:arrow-ios-back-fill" width={20} />
            </IconButton>
          </Box>
        </Scrollbar>
      </Box>

      {/* Mini content */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: sidebarOpen ? 0 : 1,
          pointerEvents: sidebarOpen ? 'none' : 'auto',
          transition: contentTransition,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 1,
          }}
        >
          <Box sx={{ pt: 2 }} />

          {!isOrganizationAdminWithoutOrganizations && userData?.onboarded && (
            <NavSectionMini data={navData} />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: 'grey.400',
                bgcolor: 'grey.800',
                '&:hover': { bgcolor: 'grey.700' },
              }}
            >
              <Iconify icon="eva:arrow-ios-forward-fill" width={20} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const sidebarTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: sidebarOpen
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  });

  const proSidebarWidth = sidebarOpen ? NAV.W_DASHBOARD : NAV.W_DASHBOARD_MINI;

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: {
          lg: isProSidebar ? proSidebarWidth : NAV.W_DASHBOARD,
        },
        transition: isProSidebar ? sidebarTransition : undefined,
      }}
    >
      {!isProSidebar && <NavToggleButton />}

      {isDesktop && isProSidebar ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: proSidebarWidth,
              bgcolor: DARK_BACKGROUND.default,
              borderRight: 'none',
              top: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
              height: `calc(100vh - ${HEADER.H_DASHBOARD_DESKTOP_OFFSET}px)`,
              overflowX: 'hidden',
              transition: sidebarTransition,
            },
          }}
        >
          {renderProSidebarContent}
        </Drawer>
      ) : isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderDefaultContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
              bgcolor: DARK_BACKGROUND.default,
            },
          }}
        >
          {renderDefaultContent}
        </Drawer>
      )}
    </Box>
  );
}
